// One TASK CARD on the board — the v2 "Signal" anatomy (Task Kanban v2): a mono status line, the title,
// and one quiet meta row. The card is the SIGNAL; the thread (the Task-detail modal) is the detail — plan,
// updates, gates, and the permanent-delete control all live there (delete sits in the modal footer — owner
// ruling 2026-07-24; the card carries exactly one control, the open). The status line DERIVES from the
// projection's own lifecycle fields (state + waitingOn + parkedReason — G-182, never a client-authored
// status): a blocked Task's status IS its whole-Task parked reason, joined by the next-check wake chip.
// The meta row shows the owner as a de-identified avatar derived from Identity's display label (NEVER the
// raw owner id, G-197)
// and the metric-link glyph OR the non-metric category (exactly one is set — G-099). A draft renders
// dashed (proposed, inert); a done or dropped Task fades. Clicking the card OPENS the Task-detail modal
// (the card only signals the open, it fetches nothing). Tokens only (G-181).

import type { BoardTaskRow } from '../projections.ts';
import { ownerInitials } from '../owner-display.ts';
import { Avatar } from '../../app/Avatar.tsx';
import { Icon } from '../../app/Icon.tsx';
import { useShell } from '../../app/ShellContext.tsx';

export interface TaskCardProps {
  readonly card: BoardTaskRow;
}

/** The status-line phrase — derived ONLY from projection fields (G-182); an unknown state shows itself. */
function statusLine(card: BoardTaskRow): string {
  switch (card.state) {
    case 'draft':
      return card.waitingOn === 'draft_reviewer'
        ? 'proposed · awaiting review'
        : 'proposed · draft';
    case 'briefed':
      return card.waitingOn === 'owner' ? 'briefed · awaiting start' : 'briefed · queued';
    case 'working':
      return 'agent working';
    case 'blocked':
      return card.parkedReason ?? 'parked';
    case 'done':
      return 'closed · done';
    case 'dropped':
      // The stop that is NOT done reads as its own ending, never as a routine archive (3 Sep 2026 ruling).
      return card.closureKind === 'unresolved' ? 'stopped · unresolved' : 'closed · archived';
    default:
      return card.state;
  }
}

/** Compact next-check display (weekday + 24h time); a non-date value renders exactly as carried. */
function shortWake(value: string): string {
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return value;
  const day = when.toLocaleDateString('en-GB', { weekday: 'short' });
  const time = when.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${day} ${time}`;
}

/** A single board Task card — one whole-card control that opens the Task-detail modal for this Task. */
export function TaskCard({ card }: TaskCardProps): React.JSX.Element {
  const shell = useShell();
  return (
    <article className="task-card" data-state={card.state}>
      <button
        type="button"
        className="task-open"
        data-card-scroll="true"
        data-open-task={card.id}
        onClick={() => shell.openTaskModal(card.id)}
        aria-label={`Open task: ${card.title}`}
      >
        <div className="tc-status">
          <span className="dot" aria-hidden="true" />
          <span className="st">{statusLine(card)}</span>
          {(card.state === 'blocked' || card.state === 'working') && card.nextCheckAt !== null ? (
            <span className="wake">
              <Icon name="clock" size={10} />
              <time dateTime={card.nextCheckAt}>{shortWake(card.nextCheckAt)}</time>
            </span>
          ) : null}
        </div>
        <div className="ti">{card.title}</div>
        <div className="mt">
          <Avatar initials={ownerInitials(card.ownerDisplayName ?? '')} size="sm" />
          <span className="spacer" aria-hidden="true" />
          {card.metricId !== null ? (
            <span className="metric-glyph" role="img" aria-label="KPI-linked">
              <Icon name="kpis" size={12} />
            </span>
          ) : (
            <span className="cat">{card.nonMetricCategory ?? 'General'}</span>
          )}
        </div>
      </button>
    </article>
  );
}
